import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Image as ImageIcon, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { BookingPagePhoto, PhotoLayout } from '../types';

interface PhotoHeaderProps {
  layout: PhotoLayout;
  photos: BookingPagePhoto[];
  orgName: string;
}

// Cover (1–2 photos) or gallery (3+). The "none" layout is handled by
// BusinessHeader, which shows a logo hero instead.
export function PhotoHeader({ layout, photos, orgName }: PhotoHeaderProps) {
  const { t } = useTranslation('bookingPage');
  const [openAt, setOpenAt] = useState<number | null>(null);
  const [mobileIndex, setMobileIndex] = useState(0);

  if (layout === 'none' || photos.length === 0) return null;

  const alt = (photo: BookingPagePhoto) => photo.alt || orgName;
  const src = (photo: BookingPagePhoto) => photo.large_url || photo.thumb_url || '';

  return (
    <>
      {/* Mobile: one photo at a time with swipe (scroll snap) and a counter */}
      <div className="relative -mx-4 sm:hidden" data-testid="photo-header" data-layout={layout}>
        <div
          className="flex snap-x snap-mandatory overflow-x-auto scrollbar-none"
          onScroll={(e) => {
            const el = e.currentTarget;
            setMobileIndex(Math.round(el.scrollLeft / el.clientWidth));
          }}
        >
          {photos.map((photo, i) => (
            <button
              key={photo.id}
              type="button"
              className="w-full shrink-0 snap-center"
              onClick={() => setOpenAt(i)}
            >
              <img src={src(photo)} alt={alt(photo)} className="h-[180px] w-full object-cover" />
            </button>
          ))}
        </div>
        {photos.length > 1 && (
          <span className="absolute bottom-3 right-4 rounded-pill bg-surface px-2.5 py-1 text-caption text-ink shadow-sm">
            {t('photos.counter', { current: mobileIndex + 1, total: photos.length })}
          </span>
        )}
      </div>

      {/* Desktop */}
      <div className="hidden sm:block" data-testid="photo-header-desktop" data-layout={layout}>
        {layout === 'cover' ? (
          <button type="button" className="block w-full overflow-hidden rounded-4xl" onClick={() => setOpenAt(0)}>
            <img src={src(photos[0])} alt={alt(photos[0])} className="h-[380px] w-full object-cover" />
          </button>
        ) : (
          <div className="relative grid h-[460px] grid-cols-[2fr_1fr] gap-2 overflow-hidden rounded-4xl">
            <button type="button" className="min-h-0" onClick={() => setOpenAt(0)}>
              <img src={src(photos[0])} alt={alt(photos[0])} className="h-full w-full object-cover" />
            </button>
            <div className="grid min-h-0 grid-rows-2 gap-2">
              <button type="button" className="min-h-0" onClick={() => setOpenAt(1)}>
                <img src={src(photos[1])} alt={alt(photos[1])} className="h-full w-full object-cover" />
              </button>
              {/* Three photos fill the bottom cell edge to edge; four or more split it in two. */}
              <div className={cn('grid min-h-0 gap-2', photos.length > 3 && 'grid-cols-2')}>
                <button type="button" className="min-h-0" onClick={() => setOpenAt(2)}>
                  <img src={src(photos[2])} alt={alt(photos[2])} className="h-full w-full object-cover" />
                </button>
                {photos.length > 3 && (
                  <button type="button" className="min-h-0" onClick={() => setOpenAt(3)}>
                    <img src={src(photos[3])} alt={alt(photos[3])} className="h-full w-full object-cover" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpenAt(0)}
                  className="absolute bottom-3 right-3 inline-flex h-10 items-center gap-2 rounded-md border border-line bg-surface px-3.5 text-[14px] font-medium text-ink shadow-sm hover:border-brand-300"
                >
                  <ImageIcon className="h-4 w-4" />
                  {t('photos.showAll', { count: photos.length })}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={openAt !== null} onOpenChange={(open) => !open && setOpenAt(null)}>
        <DialogContent className="max-w-5xl border-0 bg-ink p-0 text-white [&>button]:hidden">
          <DialogTitle className="sr-only">{t('photos.galleryTitle', { name: orgName })}</DialogTitle>
          {openAt !== null && (
            <div className="relative">
              <img src={src(photos[openAt])} alt={alt(photos[openAt])} className="max-h-[80vh] w-full rounded-lg object-contain" />
              <button
                type="button"
                aria-label={t('photos.close')}
                onClick={() => setOpenAt(null)}
                className="absolute right-3 top-3 inline-flex h-11 w-11 items-center justify-center rounded-pill bg-surface text-ink"
              >
                <X className="h-5 w-5" />
              </button>
              {photos.length > 1 && (
                <>
                  <button
                    type="button"
                    aria-label={t('photos.previous')}
                    onClick={() => setOpenAt((openAt - 1 + photos.length) % photos.length)}
                    className="absolute left-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-pill bg-surface text-ink"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    aria-label={t('photos.next')}
                    onClick={() => setOpenAt((openAt + 1) % photos.length)}
                    className="absolute right-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-pill bg-surface text-ink"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-pill bg-surface px-2.5 py-1 text-caption text-ink">
                    {t('photos.counter', { current: openAt + 1, total: photos.length })}
                  </span>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
